/**
 * Generic, domain-agnostic UI copy shared by `common/`/`ui/` composites
 * (DataTable, etc). Kept separate from `ui-strings.ts` (feature/marketing
 * copy, already past the mnr-standards 400-LOC yellow threshold and pending
 * its own decompose) and from `admin-strings.ts` (admin-console-only copy) -
 * one file per concern.
 */
export const COMMON_STRINGS = {
  LOADING: 'Loading...',
  VALIDATION: {
    /** Shared optional-URL form field error (`optionalUrlSchema`). */
    INVALID_URL: 'Enter a valid URL',
    /** Shared whole-number form field error (`optionalWholeNumber` / `requiredWholeNumber`). */
    ENTER_A_NUMBER: 'Enter a number',
  },
  /** The member-facing claim action on a partner offer (FR-114); one wording wherever an offer is claimed or previewed. */
  CLAIM_OFFER: 'Claim Offer',
  /** The member-facing email action on a partner offer (FR-117); opens the member's mail app, never shows the address. */
  CONTACT_US: 'Contact Us',
  THEME: {
    // Phrased as the action, not the current state - a toggle's accessible name
    // should say what activating it does.
    TO_LIGHT: 'Switch to light theme',
    TO_DARK: 'Switch to dark theme',
  },
  /** The eye toggle on every password field (MYN-329), named for what it does. */
  PASSWORD: {
    SHOW: 'Show password',
    HIDE: 'Hide password',
  },
  TABLE: {
    ROWS_PER_PAGE: 'Rows per page',
    SORT_BY: 'Sort by',
  },
  /**
   * Shared `ExportDialog` (MYN-135 Task 4) scope-description sentence,
   * templated so the sentence is written once rather than duplicated per
   * caller (Users, Startups, ...). `{noun}` is filled in by the caller's own
   * `noun` prop - the component itself carries no user/startup/audit
   * vocabulary - and `{count}` by the current selection size.
   */
  EXPORT_DIALOG: {
    SELECTED_SCOPE: 'Exporting {count} selected {noun}. Choose a format and which fields to include.',
    ALL_SCOPE: 'Exporting every {noun} matching the current filters. Choose a format and which fields to include.',
    /** Export button label while the download mutation is in flight. */
    EXPORTING: 'Exporting...',
  },
  /** `common/FileUploadField` copy - moved here from the startups feature (MYN-196) once a second feature (admin pages) needed the same composite. */
  UPLOAD: {
    CHOOSE_FILE: 'Choose file',
    REPLACE_FILE: 'Replace',
    UPLOADING: 'Uploading…',
    REMOVE: 'Remove',
    UPLOADED: 'File uploaded',
    ERROR: 'Upload failed. Please try again.',
    TOO_LARGE: 'That file is too large. Please choose a smaller one.',
    // The file never reached storage (offline, or blocked by the bucket's
    // CORS rules), so the file itself is not the problem (MYN-271).
    NETWORK_ERROR:
      'The upload could not reach file storage. Please check your connection and try again. If it keeps happening, contact support.',
  },
  /** `common/ImageCropDialog` copy (FR-121). */
  IMAGE_CROP: {
    TITLE: 'Crop your image',
    DESCRIPTION:
      'Drag the image, or use the arrow keys, to move it, and use the slider to zoom. The frame is fixed to the shape this image needs.',
    STAGE_LABEL: 'Crop area',
    ZOOM_LABEL: 'Zoom',
    CONFIRM: 'Use this image',
    CANCEL: 'Cancel',
    WORKING: 'Preparing image…',
    UNREADABLE: 'This image could not be read. Please choose a different PNG or JPG.',
    TOO_BIG: 'This image is too large to crop. Please choose a smaller one.',
    FAILED: 'Could not prepare this image. Please try again or choose a different one.',
  },
} as const;
